import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../services/AuthContext';
import axios from 'axios';
import './SamlLoginPage.css';

/**
 * Auth Redirect Page Component
 * Automatically checks authentication status and redirects accordingly
 */
const SamlLoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { refreshAuth } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [urlParams, setUrlParams] = useState({});
    const authCheckRef = useRef(false);
    
    // Use environment variable for API URL
    const API_URL = process.env.REACT_APP_API_BASE_URL;

    // Create API client
    const createApiClient = () => {
        return axios.create({
            baseURL: API_URL,
            withCredentials: true,
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });
    };

    // Utility function to get cookie value by name
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    // Check if authentication token exists in cookies or sessionStorage
    const isTokenAvailable = () => {
        const cookieToken = getCookie('auth_token') || getCookie('authToken') || getCookie('token') || getCookie('jwt');
        const sessionToken = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
        
        return !!(cookieToken || sessionToken);
    };

    // Extract URL parameters and check auth status immediately
    useEffect(() => {
        // Prevent multiple auth checks
        if (authCheckRef.current) {
            return;
        }
        
        authCheckRef.current = true;
        
        const checkAuthAndProceed = async () => {
            try {
                console.log('Checking authentication status...');
                
                // Dynamically extract all query parameters without altering case
                const queryParams = new URLSearchParams(location.search);
                const params = {};
                queryParams.forEach((value, key) => {
                    params[key] = value; // Keep keys as they are
                });

                // Map parameters to expected format with flexible case handling
                const extractedParams = {
                    SessionId: params.SessionId || params.sessionId || params.sessionID || '',
                    PatientId: params.PatientId || params.patientId || params.patientID || '',
                    HRAId: params.HRAId || params.hraId || params.hraID || '',
                    UserID: params.UserID || params.userId || params.userID || '',
                    MemberID: params.MemberID || params.memberId || params.memberID || ''
                };

                console.log('Extracted parameters:', extractedParams);
                
                // Require at least one primary parameter (SessionId, PatientId, or UserID)
                const isQueryEmpty = Object.keys(params).length === 0;
                const hasRequiredParam = extractedParams.SessionId || extractedParams.PatientId || extractedParams.UserID;
                
                if (isQueryEmpty || !hasRequiredParam) {
                    console.error('Missing required parameters. Need at least SessionId, PatientId, or UserID');
                    navigate('/error?reason=missing-required-params', { replace: true });
                    return;
                }

                console.log('Parameters validation passed, proceeding with auth check...');
                console.log('API_URL:', API_URL);
                console.log('Full API endpoint:', `${API_URL}/Login/check-auth-status`);

                setUrlParams(extractedParams);
                // Store these parameters in sessionStorage with standardized casing
                sessionStorage.setItem('samlRequestParams', JSON.stringify(extractedParams));

                // Check authentication status via API
                const apiClient = createApiClient();
                console.log('Making auth status API call...');
                const authStatusResponse = await apiClient.get('/Login/check-auth-status');
                console.log('Auth status response:', authStatusResponse.data);

                // Check if user is authenticated (handle both IsAuthenticated and isAuthenticated)
                const isAuthenticated = authStatusResponse.data?.IsAuthenticated || authStatusResponse.data?.isAuthenticated;

                if (isAuthenticated) {
                    console.log('User is already authenticated, redirecting to protected route');
                    console.log('SKIPPING SAML API CALL - User already authenticated');
                    // Store user info in sessionStorage
                    sessionStorage.setItem('isAuthenticated', 'true');
                    const userInfo = {
                        userName: authStatusResponse.data?.Username || authStatusResponse.data?.username || '',
                        userId: authStatusResponse.data?.UserId || authStatusResponse.data?.userId || '',
                        email: authStatusResponse.data?.Email || authStatusResponse.data?.email || '',
                        roles: authStatusResponse.data?.Roles || authStatusResponse.data?.roles || [],
                        isAuthenticated: true
                    };
                    sessionStorage.setItem('user', JSON.stringify(userInfo));
                    // Refresh auth context
                    if (refreshAuth) {
                        await refreshAuth();
                    }
                    // Check if HRAId is present in the URL (case-insensitive)
                    const urlHasHRAId = /HRAId/i.test(location.search) || !!extractedParams.HRAId;
                    if (urlHasHRAId) {
                        sessionStorage.setItem('isAssessment', 'true');
                        navigate('/CCAArchivalUI/Assessment/Index' + location.search, { replace: true });
                    } else {
                        sessionStorage.setItem('isAssessment', 'false');
                        navigate('/CCAArchivalUI/ProgressNotes/Index' + location.search, { replace: true });
                    }
                } else {
                    console.log('User is NOT authenticated, proceeding with SAML login');
                    console.log('CALLING SAML API - User needs authentication');
                    console.log('Calling initiateLogin with params:', extractedParams);
                    // Not authenticated, initiate SAML login flow
                    const urlHasHRAId = /HRAId/i.test(location.search) || !!extractedParams.HRAId;
                    await initiateLogin({ ...extractedParams, isAssessment: urlHasHRAId }, params);
                }
            } catch (error) {
                console.error('Auth check error:', error);
                setError('Authentication check failed. Please try again.');
                
                // If auth check fails, try SAML login as fallback
                const queryParams = new URLSearchParams(location.search);
                const params = {};
                queryParams.forEach((value, key) => {
                    params[key] = value;
                });
                const extractedParams = {
                    SessionId: params.SessionId || params.sessionId || params.sessionID || '',
                    PatientId: params.PatientId || params.patientId || params.patientID || '',
                    HRAId: params.HRAId || params.hraId || params.hraID || '',
                    UserID: params.UserID || params.userId || params.userID || '',
                    MemberID: params.MemberID || params.memberId || params.memberID || ''
                };
                
                // Require at least one primary parameter for fallback
                const isQueryEmpty = Object.keys(params).length === 0;
                const hasRequiredParam = extractedParams.SessionId || extractedParams.PatientId || extractedParams.UserID;
                
                if (isQueryEmpty || !hasRequiredParam) {
                    navigate('/error?reason=missing-required-params', { replace: true });
                    return;
                }
                
                // Store parameters for later use
                sessionStorage.setItem('samlRequestParams', JSON.stringify(extractedParams));
                await initiateLogin(extractedParams, params);
            } finally {
                setLoading(false);
            }
        };
        
        checkAuthAndProceed();
    }, [location.search, API_URL, navigate, refreshAuth]);

    /**
     * Initiate SAML login process
     */
    const initiateLogin = async (extractedParams, params) => {
        try {
            console.log('initiateLogin called with:', { extractedParams, params });
            
            // Require at least one primary parameter for SAML login
            const isQueryEmpty = !params || Object.keys(params).length === 0;
            const hasRequiredParam = extractedParams.SessionId || extractedParams.PatientId || extractedParams.UserID;
            
            if (isQueryEmpty || !hasRequiredParam) {
                console.error('Cannot initiate SAML login - missing required parameters');
                navigate('/error?reason=missing-required-params', { replace: true });
                return;
            }
            
            console.log('Initiating SAML login process with params:', extractedParams);
            
            // Prepare payload to match LoginRequest.cs (PascalCase, HRAID, isAssessment)
            const HRAID = extractedParams.HRAId || '';
            // Use isAssessment from extractedParams if present, otherwise fallback to HRAID presence
            const isAssessment = extractedParams.isAssessment !== undefined
                ? extractedParams.isAssessment
                : !!HRAID;
            // Store isAssessment in sessionStorage for later use
            sessionStorage.setItem('isAssessment', isAssessment ? 'true' : 'false');
            const samlPayload = {
                UserId: extractedParams.UserID || '',
                SessionId: extractedParams.SessionId || '',
                PatientId: extractedParams.PatientId || '',
                AuthType: 'SAML', // Default auth type
                HRAID: HRAID,
                isAssessment: isAssessment
            };
            
            console.log('SAML API payload:', samlPayload);
            
            // Make the SAML request
            const apiClient = createApiClient();
            const response = await apiClient.post('/Login/saml-request', samlPayload);
            
            if (response.data && response.data.redirectUrl) {
                console.log('Received SAML redirect URL:', response.data.redirectUrl);
                // Redirect to SAML IdP in the same tab
                window.location.href = response.data.redirectUrl;
            } else {
                throw new Error('Invalid SAML response from server');
            }
        } catch (err) {
            console.error('SAML login error:', err);
            setError('SAML login failed. Please try again.');
        }
    };

    // Loading spinner component
    const LoadingSpinner = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '18px'
        }}>
            <div>Authenticating...</div>
        </div>
    );

    return (
        <Container fluid className="auth-check-container">
            <Row className="justify-content-center align-items-center min-vh-100">
                <Col xs={12} className="text-center">
                    {loading ? (
                        <LoadingSpinner />
                    ) : error ? (
                        <div className="alert alert-danger">
                            {error}
                        </div>
                    ) : (
                        <div>Authentication complete</div>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default SamlLoginPage;
